import { useContext, useEffect, useMemo, useRef, useState } from "react";
import UserImg2 from "../../assets/img/bank-icon.svg";
import SkorDefaultLoader from "../../assets/img/skorDefaultLoader.gif";
import ConnectBankModal from "../../components/Onboarding/ConnectBankModal";
import useTranslator from "../../hooks/useTranslator";
import { useNavigate } from "react-router-dom";
import AuthContext from "../../context/auth/AuthContext";
import axiosInstanceInstance from "../../services/authWrapper";
import axiosInstance from "../../services/authWrapper";
import TenantContext from "../../context/tenant/TenantContext";
import { sendUniversalMail } from "../../services/sharedService";
import ProcessPassedModal from "./ProcessPassedModal";
import { trackClarityEvent } from "../../Utils/clarityReady";
import { toast, haveValue } from "../../helper/common";
import { isSkorSdkMode } from "../../helper/skorSdk";
import constants from "../../Utils/constants";
import moment from "moment";
import styles from "./BankModal.module.css";
import {
  FileInput,
  RowButton,
  Badge,
  Button,
  Icon,
  Notice,
} from "../../components/design";

const BANK_STATEMENT_EMPHASIS_PATTERN =
  /(\beuros?\b(?:\s*\(€\))?|\b3\s+[^\s.,;:!?()]+)/gi;
const BANK_STATEMENT_EMPHASIS_MATCH =
  /^(?:euros?(?:\s*\(€\))?|3\s+[^\s.,;:!?()]+)$/i;

const emphasizeBankStatementCopy = (copy) =>
  String(copy ?? "")
    .split(BANK_STATEMENT_EMPHASIS_PATTERN)
    .map((part, index) =>
      BANK_STATEMENT_EMPHASIS_MATCH.test(part) ? (
        <strong key={`${part}-${index}`}>{part}</strong>
      ) : (
        part
      )
    );

const BankModal = ({
  user,
  formHandler,
  updateUserProfile,
  isGuarantor,
  isCoTenant = false,
  loadGuarantorData,
  loadCoTenantData,
  guarantor_a_id,
  co_tenant_a_id,
  usedFor,
  setBankshow: setBankshowProp,
  loadFullInfo,
  setPaymentScoreLoading,
  reconnectFlow = false,
  getAnalysisDetails,
  setScoreValue,
  originalUser,
  setFinancial_info_errors,
}) => {
  const { _t } = useTranslator();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const { loadUser, userType, loadUserTenant, setLoading } =
    useContext(AuthContext);
  const { uploadBankStatement, sendRemoteConnectLink } =
    useContext(TenantContext);
  const [bankShow, setBankShow] = useState(false);

  const getGuarantorCompletionPath = () => {
    const returnTo = new URLSearchParams(window.location.search).get(
      "returnTo"
    );
    if (returnTo?.startsWith("/") && !returnTo.startsWith("//")) {
      return returnTo;
    }
    return `/home${
      originalUser?.gurantor_added_id?.length === 0 ? "?welcome=true" : ""
    }`;
  };
  const getCoTenantCompletionPath = () => {
    const returnTo = new URLSearchParams(window.location.search).get(
      "returnTo"
    );
    if (returnTo?.startsWith("/") && !returnTo.startsWith("//"))
      return returnTo;
    return "/home?welcome=true";
  };
  const [tink_countryCode, setTink_countryCode] = useState("");
  const [countryCodeError, setCountryCodeError] = useState("");
  const queryParameters = new URLSearchParams(window.location.search);
  const error1 = queryParameters.get("error");
  let account_verification_report_id = queryParameters.get(
    "account_verification_report_id"
  );
  let transaction_report_id = queryParameters.get("transaction_report_id");
  const [calculatingScoreLoading, setCalculatingScoreLoading] = useState(false);
  const [uploadStatementLoading, setUploadStatementLoading] = useState(false);
  const [uploadStatementStatus, setUploadStatementStatus] = useState(false);
  const [submitStepLoading, setSubmitStepLoading] = useState(false);
  const [reuploadStatement, setReuploadStatement] = useState(false);
  const [showTickScreen, setShowTickScreen] = useState(false);
  const [showBankAccountConnectedScreen, setShowBankAccountConnectedScreen] =
    useState(false);
  const [doItLaterLoading, setDoItLaterLoading] = useState(false);
  const [sendLinkLoading, setSendLinkLoading] = useState(false);
  const [sendLinkSuccess, setSendLinkSuccess] = useState(false);

  const close = () => {
    if (setBankshowProp) {
      setBankshowProp(false);
    } else {
      setBankShow(false);
    }
  };

  const isExpiredRenewal = useMemo(() => {
    if (!user?.re_new_pending) return false;
    const dateToCheck = haveValue(user?.bankVerificationDate)
      ? user.bankVerificationDate
      : user?.created_at;
    if (!dateToCheck) return false;
    const expiryDate = moment(dateToCheck).add(2, "months");
    return expiryDate.isBefore(moment(), "day");
  }, [user?.re_new_pending, user?.bankVerificationDate, user?.created_at]);

  const handleBack = () => {
    if (usedFor === "popup") {
      close();
      return;
    }
    formHandler?.prev?.();
    formHandler?.prev?.();
    formHandler?.prev?.();
  };

  const bankDataProviders = user?.bank_details?.userDataByProvider ?? [];
  const hasPersistedBankDetails = bankDataProviders.length > 0;
  const isUploadedCSV =
    bankDataProviders[0]?.providerName === "Uploaded from CSV" ||
    uploadStatementStatus;

  // In onboarding, returning to this step should show the persisted completion
  // state. Popup mode intentionally keeps the choices available for replacement.
  const shouldShowPersistedBankStatus =
    usedFor !== "popup" && hasPersistedBankDetails;
  const shouldShowOptionsScreen =
    !uploadStatementStatus &&
    !showBankAccountConnectedScreen &&
    ((usedFor === "popup" && !reuploadStatement) ||
      (!shouldShowPersistedBankStatus &&
        (reconnectFlow || isExpiredRenewal || !hasPersistedBankDetails)));

  const isCompletedForSubmit = isUploadedCSV || sendLinkSuccess;
  const isLoading =
    calculatingScoreLoading || uploadStatementLoading || sendLinkLoading;

  // Embedded flows (SDK iframe, incl. AVALON keys) must not leave the
  // onboarding page: their Tink link comes from the tenant-authenticated
  // GET /tenants/tink-link API (redirect -> /ext-bank-verification) and
  // opens in a new tab.
  const isAvalon =
    (user?.key_id?.meta_data?.key_rule_type ??
      originalUser?.key_id?.meta_data?.key_rule_type) ===
    constants.MODULAR_KEY_RULE_TYPES.AVALON;
  const isSdkIframe = isSkorSdkMode();

  const tinkCodeToCountryName = {
    BE: "belgium",
    DE: "germany",
    DK: "denmark",
    EE: "estonia",
    ES: "spain",
    FI: "finland",
    FR: "france",
    GB: "unitedKingdom",
    IT: "italy",
    LT: "lithuania",
    LV: "latvia",
    NL: "netherlands",
    NO: "norway",
    PL: "poland",
    PT: "portugal",
    SE: "sweden",
  };

  // Embedded main-tenant flow: Tink opens in a new tab and this
  // page polls for the result. The user is never blocked: Continue skips the
  // wait (same as "I'll do it later") and going back shows the verification
  // choices (bank connection or bank statement) again.
  const avalonFlow = (isAvalon || isSdkIframe) && !isGuarantor && !isCoTenant;
  const AVALON_POLL_INTERVAL_MS = 5000;
  const AVALON_MAX_POLL_ATTEMPTS = 120;
  const AVALON_MAX_CONSECUTIVE_ERRORS = 8;
  const [avalonPhase, setAvalonPhase] = useState("idle");
  const [avalonLinkLoading, setAvalonLinkLoading] = useState(false);
  const [avalonErrorDetail, setAvalonErrorDetail] = useState(null);
  const avalonPollRef = useRef(null);
  const avalonAttemptsRef = useRef(0);
  const avalonErrorsRef = useRef(0);
  const avalonPhaseRef = useRef("idle");
  const setAvalonPhaseBoth = (phase) => {
    avalonPhaseRef.current = phase;
    setAvalonPhase(phase);
  };

  const stopAvalonPolling = () => {
    if (avalonPollRef.current) {
      clearInterval(avalonPollRef.current);
      avalonPollRef.current = null;
    }
  };

  const refreshAfterAvalonSuccess = async () => {
    try {
      if (usedFor === "popup") {
        if (typeof setFinancial_info_errors === "function") {
          setFinancial_info_errors(null);
        }
        if (typeof setPaymentScoreLoading === "function") {
          setPaymentScoreLoading(true);
        }
        if (loadFullInfo) {
          await loadFullInfo(false);
        }
        if (getAnalysisDetails) {
          getAnalysisDetails();
        }
      } else {
        loadUserTenant(localStorage.getItem("usertype"));
      }
    } catch (err) {
      console.log("Error refreshing after bank connect:", err);
    }
  };

  const checkAvalonBankStatus = async (tenantId) => {
    avalonAttemptsRef.current += 1;
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/tenants/checkForPaymentScoreData?tenant_id=${tenantId}`
      );
      avalonErrorsRef.current = 0;
      if (res?.data?.havePaymentScore) {
        stopAvalonPolling();
        await refreshAfterAvalonSuccess();
        setShowBankAccountConnectedScreen(true);
        setAvalonPhaseBoth("success");
        return;
      }
      if (haveValue(res?.data?.financial_info_errors)) {
        stopAvalonPolling();
        setAvalonErrorDetail("bank");
        if (typeof setFinancial_info_errors === "function") {
          setFinancial_info_errors(res.data.financial_info_errors);
        }
        setAvalonPhaseBoth("error");
        return;
      }
      if (avalonAttemptsRef.current >= AVALON_MAX_POLL_ATTEMPTS) {
        stopAvalonPolling();
        setAvalonPhaseBoth("timeout");
      }
    } catch (err) {
      avalonErrorsRef.current += 1;
      if (avalonErrorsRef.current >= AVALON_MAX_CONSECUTIVE_ERRORS) {
        stopAvalonPolling();
        setAvalonErrorDetail("network");
        setAvalonPhaseBoth("error");
      }
    }
  };

  const startAvalonPolling = (tenantId) => {
    stopAvalonPolling();
    avalonAttemptsRef.current = 0;
    avalonErrorsRef.current = 0;
    setAvalonErrorDetail(null);
    setAvalonPhaseBoth("waiting");
    checkAvalonBankStatus(tenantId);
    avalonPollRef.current = setInterval(
      () => checkAvalonBankStatus(tenantId),
      AVALON_POLL_INTERVAL_MS
    );
  };

  const openAvalonTinkLink = async () => {
    const country = tinkCodeToCountryName[tink_countryCode];
    if (!country) {
      setCountryCodeError(_t("pleaseSelectCountryCode"));
      return false;
    }
    const tenantId = user?._id;
    if (!tenantId) {
      toast.error(_t("userNotAuthorized"));
      return false;
    }
    setAvalonLinkLoading(true);
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_SERVER_URL}/tenants/tink-link?country=${country}`
      );
      const url = res?.data?.success_res?.url ?? res?.data?.data?.url ?? null;
      if (!url) {
        toast.error(_t("errorSendingTinkLink"));
        return false;
      }
      setBankShow(false);
      window.open(url, "_blank", "noopener,noreferrer");
      startAvalonPolling(tenantId);
      return true;
    } catch (err) {
      toast.error(_t("errorSendingTinkLink"));
      return false;
    } finally {
      setAvalonLinkLoading(false);
    }
  };

  // Back to the verification choices (bank connection or bank statement)
  // so the user can start over. Polling stops; nothing is auto-minted.
  const backToBankOptions = () => {
    stopAvalonPolling();
    setAvalonErrorDetail(null);
    setAvalonPhaseBoth("idle");
  };

  const handleAvalonSuccessContinue = () => {
    if (usedFor === "popup") {
      close();
      return;
    }
    handleSubmitStep();
  };

  // Never leave a poll running after unmount.
  useEffect(() => () => stopAvalonPolling(), []);

  // Resume waiting if the user reloads mid-flow (bank leg started elsewhere,
  // no score and no error yet). CSV uploads are excluded: they are already
  // complete and must not poll.
  useEffect(() => {
    if (!avalonFlow) return;
    if (avalonPhaseRef.current !== "idle") return;
    if (!user?._id || user?.bank_status !== true) return;
    if (isUploadedCSV) return;
    if (haveValue(user?.paymentScores)) return;
    if (haveValue(user?.financial_info_errors)) return;
    startAvalonPolling(user._id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avalonFlow, user?._id, user?.bank_status, isUploadedCSV]);

  useEffect(() => {
    if (error1) {
      toast.error(_t("bankDetailsCannotBeFetched"));
    }
    if (account_verification_report_id && transaction_report_id) {
      let userid = user?._id;
      if (!userid) {
        toast.error(_t("userNotAuthorized"));
      } else {
        calculateScore(
          userid,
          account_verification_report_id,
          transaction_report_id
        );
      }
    }
  }, [account_verification_report_id]);

  const calculateScore = async (
    userid,
    accountverificationid,
    transactionreportid
  ) => {
    account_verification_report_id = null;
    transaction_report_id = null;
    await clearPaymentData(userid);
    if (isGuarantor) {
      loadGuarantorData("applyProfile", null, false);
    } else if (isCoTenant) {
      loadCoTenantData?.("applyProfile", null, false);
    } else {
      loadUserTenant(localStorage.getItem("usertype"));
    }
    calculatePaymentScore(userid, accountverificationid, transactionreportid);
  };

  const calculatePaymentScore = async (
    userid,
    accountverificationid,
    transactionreportid
  ) => {
    let url = `${process.env.REACT_APP_SERVER_URL}/tenants/calculatescore?tenantid=${userid}&accountverificationid=${accountverificationid}&transactionreportid=${transactionreportid}`;
    if (isGuarantor) {
      url += "&guarantor=true";
    }
    if (isGuarantor || isCoTenant) {
      setShowTickScreen(true);
    } else {
      setShowBankAccountConnectedScreen(true);
    }

    setTimeout(async () => {
      if (isGuarantor) {
        sendGuarantorRequest();
      } else if (isCoTenant) {
        handleSubmitStep();
      } else {
        handleSubmitStep();
        if (window.location.pathname === "/onboarding") {
          trackClarityEvent("tenant_bank_connect_step_ended");
          setTimeout(() => {
            trackClarityEvent("tenant_guarantor_step_started");
          }, constants.CLARITY_DELAY);
        }
      }
    }, 2000);

    axiosInstanceInstance
      .get(url)
      .then((res) => {
        let serverResponse = res.data;
        if (serverResponse.status !== "success") {
          if (serverResponse.status == "pending") {
          } else {
            trackClarityEvent("tenant_bank_connect_failed");
            toast.error(_t("skorCanNotBeCalculated"));
          }
        }
        if (serverResponse.status === "success") {
          if (isGuarantor) {
            loadGuarantorData("applyProfile", null, false);
          } else if (isCoTenant) {
            loadCoTenantData?.("applyProfile", null, false);
          } else {
            trackClarityEvent("tenant_bank_connected");
            loadUser(userType);
          }
        }
        const url = new URL(window.location);
        url.searchParams.delete("account_verification_report_id");
        url.searchParams.delete("transaction_report_id");
        window.history.replaceState(
          {},
          document.title,
          url.pathname + url.search
        );
      })
      .catch(() => {
        setCalculatingScoreLoading(false);
      });
  };

  const clearPaymentData = async (tenantid) => {
    return new Promise((resolve) => {
      let url = `${process.env.REACT_APP_SERVER_URL}/tenants/algoan-info?tenantid=${tenantid}`;
      if (isGuarantor) {
        url += "&guarantor=true";
      }
      axiosInstance
        .delete(url)
        .then((res) => {
          return resolve(true);
        })
        .catch((err) => {
          let err_res = err;
          toast.error(err_res.message);
          return resolve(null);
        });
    });
  };

  const openBankModal = () => {
    setBankShow(true);
  };
  const BankhandleClose = () => {
    close();
  };

  const changeCountryCode = (e) => {
    setTink_countryCode(e.target.value);
  };

  const connectToBank = async () => {
    if (!tink_countryCode) {
      setCountryCodeError(_t("pleaseSelectCountryCode"));
      return;
    } else {
      setCountryCodeError("");
    }
    // Embedded (SDK iframe / Avalon): mint the Tink link server-side
    // (redirect -> /ext-bank-verification) and open it in a new tab so the
    // onboarding page stays open behind it. Polling for the result starts
    // inside openAvalonTinkLink.
    if (avalonFlow) {
      await openAvalonTinkLink();
      return;
    }
    var lang = "en_US";
    if (localStorage.getItem("i18nextLng") == "es") {
      lang = "es_ES";
    }

    let baseUrl = `https://link.tink.com/1.0/reports/create-report?client_id=${process.env.REACT_APP_TINK_CLIENT_ID}&market=${tink_countryCode}&locale=${lang}&report_types=TRANSACTION_REPORT,ACCOUNT_VERIFICATION_REPORT&refreshable_items=IDENTITY_DATA,CHECKING_ACCOUNTS,SAVING_ACCOUNTS,CHECKING_TRANSACTIONS,SAVING_TRANSACTIONS&account_dialog_type=SINGLE`;

    let tinkUrlLiveFront;

    if (usedFor === "popup") {
      if (isGuarantor) {
        tinkUrlLiveFront = `${baseUrl}&redirect_uri=https%3A%2F%2F${process.env.REACT_APP_URL_NAME}%2Fhome?guarantor=${user?._id}`;
      } else if (isCoTenant) {
        tinkUrlLiveFront = `${baseUrl}&redirect_uri=https%3A%2F%2F${process.env.REACT_APP_URL_NAME}%2Fhome?co_tenant=${user?._id}`;
      } else {
        tinkUrlLiveFront = `${baseUrl}&redirect_uri=https%3A%2F%2F${process.env.REACT_APP_URL_NAME}%2Fhome`;
      }
    } else {
      if (isGuarantor) {
        tinkUrlLiveFront = `${baseUrl}&redirect_uri=https%3A%2F%2F${process.env.REACT_APP_URL_NAME}%2Fguarantor%2Fonboarding?guarantor_a_id=${guarantor_a_id}`;
      } else if (isCoTenant) {
        tinkUrlLiveFront = `${baseUrl}&redirect_uri=https%3A%2F%2F${process.env.REACT_APP_URL_NAME}%2Fco-tenant%2Fonboarding?co_tenant_a_id=${co_tenant_a_id}`;
      } else {
        tinkUrlLiveFront = `${baseUrl}&redirect_uri=https%3A%2F%2F${process.env.REACT_APP_URL_NAME}%2Fonboarding`;
      }
    }

    window.open(tinkUrlLiveFront, "_self");
  };

  const handleTempUploadFile = async (e) => {
    const files = e.target.files;
    let file = files[0];
    const formData = new FormData();
    formData.append("file", file);
    formData.append("tenant_id", user?._id);
    try {
      if (usedFor === "popup") {
        await updateUserProfile({
          bank_status: true,
          paymentScores: null,
          financial_info_errors: null,
          role: "tenant",
        });
        setFinancial_info_errors(null);
        setScoreValue(null);
        setPaymentScoreLoading(true);
        await loadFullInfo(false);
        getAnalysisDetails();
      }
      setUploadStatementLoading(true);
      uploadBankStatement(formData);
      setUploadStatementStatus(true);
      setReuploadStatement(true);
    } catch (err) {
      console.log("Error: ", err);
    } finally {
      setUploadStatementLoading(false);
    }
  };

  const sendGuarantorRequest = async () => {
    await sendMail(user?.email, "score");
  };

  const sendMail = async (receiverEmail, emailType) => {
    let payload = {
      receiverEmail: receiverEmail,
      senderId: localStorage.getItem("saveduserid"),
      emailType: emailType,
      status: "processing",
    };
    if (haveValue(guarantor_a_id)) {
      payload.guarantorRequestId = guarantor_a_id;
    }
    try {
      await sendUniversalMail(payload);
      toast.success(_t("emailSent"));
    } catch (err) {
      toast.error(_t("errorSendingMail"), err?.message);
      throw err;
    }
  };

  const handleDoItLater = async () => {
    if (doItLaterLoading) {
      return;
    }
    setLoading(true);
    let formData = {};
    if (isGuarantor || isCoTenant || user?.gurantor_for?.length > 0) {
      formData = {
        profile_completed: true,
        role: localStorage.getItem("usertype"),
        is_edit: false,
        lang: localStorage.getItem("i18nextLng"),
      };
    } else {
      formData = {
        step: 7,
        role: localStorage.getItem("usertype"),
      };
      trackClarityEvent("tenant_bank_connect_step_skipped");
      setTimeout(() => {
        trackClarityEvent("tenant_guarantor_step_started");
      }, constants.CLARITY_DELAY);
    }

    try {
      setDoItLaterLoading(true);
      await updateUserProfile(formData);

      if (isGuarantor) {
        await sendGuarantorRequest();
        navigate(getGuarantorCompletionPath());
      } else if (isCoTenant) {
        navigate(getCoTenantCompletionPath());
      } else if (user?.gurantor_for?.length > 0) {
        navigate(`/home`);
      }
    } catch (err) {
    } finally {
      setLoading(false);
      setDoItLaterLoading(false);
    }
  };

  const handleSubmitStep = async (sendRequest = false) => {
    try {
      setSubmitStepLoading(true);
      let formData = {};
      if (isGuarantor || isCoTenant || user?.gurantor_for?.length > 0) {
        formData = {
          profile_completed: true,
          role: "tenant",
          is_edit: false,
        };
      } else {
        formData = {
          step: 7,
          role: localStorage.getItem("usertype"),
        };
      }

      await updateUserProfile(formData);

      if (sendRequest && isGuarantor) {
        await sendGuarantorRequest();
      }

      if (isGuarantor) {
        navigate(getGuarantorCompletionPath());
      } else if (isCoTenant) {
        navigate(getCoTenantCompletionPath());
      } else if (user?.gurantor_for?.length > 0) {
        navigate(`/home`);
      }
    } catch (err) {
      console.log("Error", err);
    } finally {
      setSubmitStepLoading(false);
    }
  };

  const sendRemoteConnectToBank = async () => {
    setSendLinkLoading(true);

    try {
      const payload = {
        guarantor_id: user?._id,
      };
      const response = await sendRemoteConnectLink(payload);
      if (response?.success) {
        setSendLinkSuccess(true);
      }
    } catch (error) {
      toast.error(_t("errorSendingTinkLink"));
    } finally {
      setSendLinkLoading(false);
      close();
    }
  };

  return (
    <>
      <div
        className={[
          "popup-overlay",
          "wrapper-modal",
          "modal-fixed-screen",
          styles.overlay,
        ].join(" ")}
        onClick={usedFor === "popup" ? () => close() : null}
      >
        {!bankShow && (
          <section
            className={`${styles.bankPanel} onboarding-modal-surface`}
            aria-labelledby="verify-income-title"
            onClick={(event) => event.stopPropagation()}
          >
            <Button
              variant="ghost"
              size="sm"
              className={styles.backButton}
              onClick={handleBack}
              iconStart={<Icon name="chevronLeft" size={16} />}
            >
              {_t("goBack")}
            </Button>

            {usedFor === "popup" && (
              <Button
                variant="secondary"
                size="sm"
                iconOnly
                className={styles.closeButton}
                onClick={close}
                aria-label={_t("close", "Close")}
                iconStart={<Icon name="close" size={16} />}
              />
            )}

            <div className={styles.bankIcon} aria-hidden="true">
              <img src={UserImg2} alt="" />
            </div>

            {/* Keep original header variants but with compact new styling */}
            {isExpiredRenewal &&
            !showBankAccountConnectedScreen &&
            !uploadStatementStatus ? (
              <header className={styles.header}>
                <h1 id="verify-income-title">
                  {_t("financialCheck", "Financial check")}
                </h1>
                <p>
                  {_t(
                    "oneLastStepToFinishUpdatingYourProfile",
                    "One last step to finish updating your profile."
                  )}
                </p>
              </header>
            ) : (
              <header className={styles.header}>
                <h1 id="verify-income-title">
                  {_t("verifyIncome", "Verify your income")}
                </h1>
                <p>
                  {_t(
                    "verifiedIncomeMakesYourProfileStandOut",
                    "With your income verified, your profile stands out to landlords. It takes about 2 minutes."
                  )}
                </p>
              </header>
            )}

            {isExpiredRenewal &&
              !uploadStatementStatus &&
              !showBankAccountConnectedScreen && (
                <Notice tone="warning" className={styles.warning}>
                  {_t(
                    "evenThoughYouConnectedYourBankBeforeThatConnectionHasExpired",
                    "Your previous bank connection has expired. Choose an option below to update your financial information."
                  )}
                </Notice>
              )}

            <div className={styles.options}>
              {avalonFlow && avalonPhase !== "idle" ? (
                <>
                  {avalonPhase === "waiting" && (
                    <>
                      <div className={styles.loadingState} role="status">
                        <img src={SkorDefaultLoader} alt="" />
                        <span>
                          {_t(
                            "avalonBankWaitingTitle",
                            "Waiting for your bank connection…"
                          )}
                        </span>
                      </div>
                      <div className={styles.avalonActions}>
                        <Button variant="secondary" onClick={backToBankOptions}>
                          {_t(
                            "backToVerificationOptions",
                            "Back to verification options"
                          )}
                        </Button>
                      </div>
                      <p className={styles.later}>
                        <Button
                          variant="link"
                          onClick={handleDoItLater}
                          loading={doItLaterLoading}
                        >
                          {_t(
                            "continueWithoutConnecting",
                            "Continue without connecting"
                          )}
                        </Button>{" "}
                        {_t(
                          "youCanDoItFromYourProfile",
                          "You can do it from your profile"
                        )}
                      </p>
                    </>
                  )}
                  {avalonPhase === "success" && (
                    <>
                      <div className={styles.loadingState} role="status">
                        <Badge>
                          <Icon name="check" size={12} />
                          {_t("bankAccountConnected")}
                        </Badge>
                        <span>
                          {_t(
                            "avalonBankSuccessBody",
                            "Your bank is connected. Continue to the next step."
                          )}
                        </span>
                      </div>
                      <Button
                        size="lg"
                        className={styles.submitButton}
                        onClick={handleAvalonSuccessContinue}
                        loading={submitStepLoading}
                      >
                        {_t("continue", "Continue")}
                      </Button>
                    </>
                  )}
                  {(avalonPhase === "error" || avalonPhase === "timeout") && (
                    <>
                      <Notice
                        tone={avalonPhase === "timeout" ? "warning" : "danger"}
                      >
                        {avalonPhase === "timeout"
                          ? _t(
                              "avalonBankTimeoutBody",
                              "Still no result — the bank tab may have been closed. Go back and choose a verification method again, or continue without connecting."
                            )
                          : avalonErrorDetail === "network"
                            ? _t(
                                "avalonBankNetworkError",
                                "We lost connection while checking your bank status. Try again or continue without connecting."
                              )
                            : _t(
                                "avalonBankConnectFailed",
                                "Your bank could not be connected. Try again or continue without connecting."
                              )}
                      </Notice>
                      <div className={styles.avalonActions}>
                        <Button onClick={backToBankOptions}>
                          {_t(
                            "backToVerificationOptions",
                            "Back to verification options"
                          )}
                        </Button>
                      </div>
                      <p className={styles.later}>
                        <Button
                          variant="link"
                          onClick={handleDoItLater}
                          loading={doItLaterLoading}
                        >
                          {_t(
                            "continueWithoutConnecting",
                            "Continue without connecting"
                          )}
                        </Button>{" "}
                        {_t(
                          "youCanDoItFromYourProfile",
                          "You can do it from your profile"
                        )}
                      </p>
                    </>
                  )}
                </>
              ) : (
                <>
                  {isLoading ? (
                    <div className={styles.loadingState} role="status">
                      <img src={SkorDefaultLoader} alt="" />
                      <span>
                        {sendLinkLoading
                          ? _t("sendingLinkToGuarantor", "Sending link…")
                          : _t("processing", "Processing…")}
                      </span>
                    </div>
                  ) : shouldShowOptionsScreen ? (
                    <>
                      {calculatingScoreLoading ? (
                        <div className={styles.loadingState} role="status">
                          <img src={SkorDefaultLoader} alt="" />
                          <span>{_t("processing", "Processing…")}</span>
                        </div>
                      ) : (
                        <>
                          <RowButton
                            type="button"
                            className={`${styles.option} ${styles.primaryOption}`}
                            onClick={
                              isGuarantor
                                ? () => fileInputRef.current?.click()
                                : openBankModal
                            }
                          >
                            <span className={styles.optionCopy}>
                              <span className={styles.optionHeading}>
                                <strong>
                                  {isGuarantor
                                    ? _t(
                                        "uploadBankStatement",
                                        "Text used in guarantor screen at onboarding flow"
                                      )
                                    : isExpiredRenewal
                                      ? _t(
                                          "connectBankDigitally",
                                          "Connect bank digitally"
                                        )
                                      : _t(
                                          "verifyBankDigitally",
                                          "Text used in guarantor screen at onboarding flow"
                                        )}
                                </strong>
                                {!isGuarantor && (
                                  <Badge tone="onDark">
                                    {_t("fastest", "Fastest")}
                                  </Badge>
                                )}
                              </span>
                              <span className={styles.optionDescription}>
                                {isGuarantor
                                  ? emphasizeBankStatementCopy(
                                      _t(
                                        "bankStatementWithOnlyEuroAccountsAndShouldNotBeOlderThan3Months",
                                        "Text used in guarantor screen at onboarding flow"
                                      )
                                    )
                                  : _t(
                                      "getYourIncomeVerifiedDigitally",
                                      isExpiredRenewal
                                        ? "Get your income verified digitally"
                                        : "Text used in guarantor screen at onboarding flow"
                                    )}
                              </span>
                              {!isGuarantor && (
                                <span className={styles.optionMeta}>
                                  {_t("countries")}:{" "}
                                  {[
                                    "belgium",
                                    "germany",
                                    "denmark",
                                    "estonia",
                                    "spain",
                                    "finland",
                                    "france",
                                    "united kingdom",
                                    "italy",
                                    "lithuania",
                                    "latvia",
                                    "netherlands",
                                    "norway",
                                    "poland",
                                    "portugal",
                                    "sweden",
                                  ].map((country, index, arr) => (
                                    <span key={country}>
                                      {_t(country)}
                                      {index !== arr.length - 1 && ", "}
                                    </span>
                                  ))}
                                </span>
                              )}
                            </span>
                            <Icon name="arrowRight" className={styles.arrow} />
                          </RowButton>

                          {!isGuarantor ? (
                            <RowButton
                              type="button"
                              className={`${styles.option} ${styles.secondaryOption}`}
                              onClick={() => fileInputRef.current?.click()}
                            >
                              <span className={styles.optionCopy}>
                                <strong>
                                  {_t(
                                    "uploadBankStatement",
                                    isExpiredRenewal
                                      ? "Upload bank statement"
                                      : "Text used in guarantor screen at onboarding flow"
                                  )}
                                </strong>
                                <span className={styles.optionDescription}>
                                  {emphasizeBankStatementCopy(
                                    isExpiredRenewal
                                      ? _t(
                                          "uploadYourLast3Months",
                                          "Upload your last 3 months"
                                        )
                                      : _t(
                                          "bankStatementWithOnlyEuroAccountsAndShouldNotBeOlderThan3Months",
                                          "Text used in guarantor screen at onboarding flow"
                                        )
                                  )}
                                </span>
                              </span>
                              <Icon
                                name="arrowRight"
                                className={styles.arrow}
                              />
                            </RowButton>
                          ) : sendLinkSuccess ? (
                            <div
                              className={`${styles.option} ${styles.secondaryOption} ${styles.statusOption}`}
                              role="status"
                            >
                              <span className={styles.optionCopy}>
                                <strong>
                                  {_t(
                                    reconnectFlow
                                      ? "linkSentSuccessfully"
                                      : "linkWillBeSentWhenGuarantorAcceptsRequest"
                                  )}
                                </strong>
                              </span>
                              <Icon name="check" className={styles.arrow} />
                            </div>
                          ) : (
                            <RowButton
                              type="button"
                              className={`${styles.option} ${styles.secondaryOption}`}
                              onClick={
                                sendLinkLoading
                                  ? undefined
                                  : sendRemoteConnectToBank
                              }
                            >
                              <span className={styles.optionCopy}>
                                <strong>
                                  {sendLinkLoading
                                    ? _t("sendingLinkToGuarantor")
                                    : _t(
                                        reconnectFlow
                                          ? "sendBankConnectLinkToGuarantor"
                                          : "sendBankVerificationLinkWhenGuarantorAccepts"
                                      )}
                                </strong>
                              </span>
                              <Icon
                                name="arrowRight"
                                className={styles.arrow}
                              />
                            </RowButton>
                          )}
                        </>
                      )}
                    </>
                  ) : isUploadedCSV ? (
                    <div
                      className={`${styles.option} ${styles.secondaryOption} ${styles.statusOption}`}
                      role="status"
                    >
                      <span className={styles.optionCopy}>
                        <span className={styles.optionHeading}>
                          <strong>
                            {_t(
                              "uploadBankStatement",
                              "Text used in guarantor screen at onboarding flow"
                            )}
                          </strong>
                          <Badge>
                            <Icon name="check" size={12} />
                            {_t("bankStatementAdded")}
                          </Badge>
                        </span>
                        <span className={styles.optionDescription}>
                          {emphasizeBankStatementCopy(
                            _t(
                              "bankStatementWithOnlyEuroAccountsAndShouldNotBeOlderThan3Months",
                              "Text used in guarantor screen at onboarding flow"
                            )
                          )}
                        </span>
                      </span>
                    </div>
                  ) : (
                    <div
                      className={`${styles.option} ${styles.secondaryOption} ${styles.statusOption}`}
                      role="status"
                    >
                      <span className={styles.optionCopy}>
                        <span className={styles.optionHeading}>
                          <strong>
                            {_t(
                              "verifyBankDigitally",
                              "Text used in guarantor screen at onboarding flow"
                            )}
                          </strong>
                          <Badge>
                            <Icon name="check" size={12} />
                            {_t("bankAccountConnected")}
                          </Badge>
                        </span>
                        <span className={styles.optionDescription}>
                          {_t(
                            "getYourIncomeVerifiedDigitally",
                            "Text used in guarantor screen at onboarding flow"
                          )}
                        </span>
                      </span>
                    </div>
                  )}
                  <FileInput
                    ref={fileInputRef}
                    accept="application/pdf,text/csv"
                    id="bankStatementFile"
                    className={styles.fileInput}
                    onChange={handleTempUploadFile}
                  />
                </>
              )}
            </div>

            {/* Original submit logic: show only when CSV/upload or sendLinkSuccess */}
            {!(avalonFlow && avalonPhase !== "idle") &&
              (isCompletedForSubmit ? (
                <Button
                  size="lg"
                  className={styles.submitButton}
                  onClick={() => {
                    if (usedFor === "popup") return close();
                    if (isGuarantor) return setShowTickScreen(true);
                    handleSubmitStep();
                  }}
                  loading={submitStepLoading}
                >
                  {submitStepLoading ? `${_t("submitting")}...` : _t("submit")}
                </Button>
              ) : (
                !isLoading && (
                  <p className={styles.later}>
                    <Button
                      variant="link"
                      onClick={handleDoItLater}
                      loading={doItLaterLoading}
                    >
                      {isSdkIframe
                        ? _t("skipThisStep")
                        : _t("iWillDoItLater", "I'll do it later")}
                    </Button>{" "}
                    {!isSdkIframe &&
                      _t(
                        "youCanDoItFromYourProfile",
                        "You can do it from your profile"
                      )}
                  </p>
                )
              ))}

            <p className={styles.securityNote}>
              <Icon name="lock" size={16} />
              {_t(
                "encryptedConnectionThroughTink",
                "Encrypted connection through Tink (Visa), regulated under PSD2."
              )}
            </p>
          </section>
        )}
        <ConnectBankModal
          open={bankShow}
          onClose={BankhandleClose}
          title={_t("connectBank")}
          buttonLabel={_t("connectBank")}
          country={tink_countryCode}
          onCountryChange={changeCountryCode}
          error={countryCodeError}
          onConnect={connectToBank}
          loading={avalonLinkLoading}
          stopClicks={usedFor === "popup" || isGuarantor}
        />
        <ProcessPassedModal
          heading={
            isGuarantor
              ? `${_t("guarantorsOnboradingSuccessful")}`
              : _t("onboardingSuccessful")
          }
          subheading={
            isGuarantor
              ? `${_t(
                  "guarantorsOnboardingHasBeenSuccessfullyCompletedMailhasbeensenton"
                )} ${user?.email}`
              : _t("yourOnboardingHasBeenSuccessfullyCompleted")
          }
          showPopup={showTickScreen}
          buttonText={"goHome"}
          handleClick={() => handleSubmitStep(true)}
        />
      </div>
    </>
  );
};

export default BankModal;
